/**
 * Hook: Load onboarding status (GET /api/v3/onboarding/status) for the dashboard.
 * Does not call getWizardStatus (which would trigger GET /api/web-api/facilities).
 * Builds a synthetic wizardStatus from onboarding response so step cards and navigation still work.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { getOnboardingStatus } from '../../../services/onboardingService';
import { ORGANIZATION_ID_STORAGE_KEY } from '../constants';

/** Build wizardStatus shape from v3 onboarding/status so StepCard and useStepNavigation work without getWizardStatus. */
function wizardStatusFromOnboarding(onboarding) {
  const stepsData = onboarding?.steps ?? onboarding?.data?.steps ?? {};
  const step1 = stepsData.step1_organization_structure ?? stepsData.facility_locations;
  const step3 = stepsData.step3_cleaning_standards;
  const step1Completed = typeof step1 === 'boolean' ? step1 : step1?.completed === true;
  const step3Completed = typeof step3 === 'boolean' ? step3 : step3?.completed === true;
  return {
    steps: [
      { step: 1, isEnabled: true, isCompleted: step1Completed, stats: {} },
      { step: 2, isEnabled: step1Completed, isCompleted: false, stats: {} },
      { step: 3, isEnabled: step1Completed, isCompleted: step3Completed, stats: {} },
    ],
  };
}

/**
 * @param {string|null} organizationId
 * @param {string} locationPathname - Used as dependency to refetch when user navigates to dashboard.
 * @param {Object|null} [initialOnboardingData] - When set (e.g. from Redux after login), used for first paint only; we still refetch so cards stay fresh on reload or "Back to overview".
 */
export function useDashboardData(organizationId, locationPathname, initialOnboardingData = null) {
  const [onboardingStatus, setOnboardingStatus] = useState(initialOnboardingData ?? null);
  const [loading, setLoading] = useState(!initialOnboardingData);

  const loadDashboardData = useCallback(async (silent = false) => {
    const orgId = organizationId || localStorage.getItem(ORGANIZATION_ID_STORAGE_KEY);
    if (!orgId) {
      setLoading(false);
      return;
    }
    try {
      if (!silent) setLoading(true);
      const onboarding = await getOnboardingStatus().catch(() => null);
      setOnboardingStatus(onboarding);
    } catch {
      setOnboardingStatus(null);
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    if (organizationId) {
      loadDashboardData(!!initialOnboardingData);
    } else {
      const storedOrgId = localStorage.getItem(ORGANIZATION_ID_STORAGE_KEY);
      if (storedOrgId) {
        loadDashboardData(!!initialOnboardingData);
      } else {
        setLoading(false);
      }
    }
  }, [organizationId, locationPathname, loadDashboardData, initialOnboardingData]);

  const wizardStatus = useMemo(
    () => (onboardingStatus ? wizardStatusFromOnboarding(onboardingStatus) : null),
    [onboardingStatus],
  );

  return {
    wizardStatus,
    onboardingStatus,
    loading,
  };
}
